// Aviso do iPhone nas Notificações Push (Pedro, 31/10/2026, T-0007): o passo muda com o jeito que o iPhone abriu o
// painel. Acessos de iPhone a Pedidos (23 a 30/10): Instagram 30%, Safari iOS 26 29%, Safari iOS 16.4–18 16%, Chrome 15%,
// Facebook 5%. Dentro do Instagram e do Facebook não há Safari e a notificação não funciona; no Safari do iOS 26 o
// Compartilhar fica dentro do ⋯. Fonte única do painel (Configurações e a faixa de Pedidos) e da Atendente
// (GET /api/atendente/conta).

export type TipoDoPainelIphone =
  | "icone"
  | "instagram"
  | "facebook"
  | "outro_app"
  | "safari_26"
  | "safari_16"
  | "chrome"
  | "outro_navegador"
  | "ios_antigo";

/**
 * Como este iPhone/iPad abriu o painel; null fora do iOS. O Safari do iOS 26 mostra a versão em `Version/26` e congela o
 * sistema em `OS 18_x`; o Instagram e o Facebook mandam o iOS de verdade.
 */
export function tipoDoPainelIphone(ua: string, opts: { standalone: boolean; maxTouchPoints?: number }): TipoDoPainelIphone | null {
  const ios = /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && (opts.maxTouchPoints ?? 0) > 1);
  if (!ios) return null;
  // Antes do 16.4 não há notificação nem pelo ícone.
  const os = ua.match(/OS (\d+)_(\d+)/);
  if (os) {
    const maior = Number(os[1]);
    const menor = Number(os[2]);
    if (maior < 16 || (maior === 16 && menor < 4)) return "ios_antigo";
  }
  if (opts.standalone) return "icone";
  if (/Instagram/i.test(ua)) return "instagram";
  if (/FBAN|FBAV|FB_IAB|FBIOS|FBSS/.test(ua)) return "facebook";
  if (/CriOS/.test(ua)) return "chrome";
  if (/FxiOS|EdgiOS|OPiOS|OPT\//.test(ua)) return "outro_navegador";
  const safari = ua.match(/Version\/(\d+)[\d.]*.*Safari\//);
  if (safari) return Number(safari[1]) >= 26 ? "safari_26" : "safari_16";
  // Navegador de dentro de outro aplicativo (sem «Safari/» no fim).
  return "outro_app";
}

export interface AvisoDoPainelIphone {
  titulo: string;
  texto: string | null;
  passos: string[];
  /** Mostrar o «Copiar link» (dentro de outro aplicativo: abrir no Safari). */
  copiarLink: boolean;
}

const PASSO_DO_ICONE = "Abra o painel pelo ícone novo e toque em Ativar Notificações.";

/** O aviso de cada jeito de abrir; null pelo ícone (ali o botão Ativar Notificações já aparece). */
export function avisoDoPainelIphone(tipo: TipoDoPainelIphone): AvisoDoPainelIphone | null {
  switch (tipo) {
    case "icone":
      return null;
    case "instagram":
    case "facebook":
    case "outro_app": {
      const app = tipo === "instagram" ? "do Instagram" : tipo === "facebook" ? "do Facebook" : "de outro aplicativo";
      return {
        titulo: "Abra o painel no Safari para receber notificações",
        texto: `Dentro ${app} as notificações não funcionam.`,
        passos: ["Toque em Copiar link.", "Abra o Safari, cole o link e entre com o código.", "Lá aparecem os passos para ativar."],
        copiarLink: true,
      };
    }
    case "safari_26":
      return {
        titulo: "Para receber notificações neste iPhone",
        texto: null,
        passos: [
          "Toque em ⋯ (embaixo, ao lado do endereço) e em Compartilhar.",
          "Toque em Adicionar à Tela de Início, com «Abrir como App Web» ligado.",
          PASSO_DO_ICONE,
        ],
        copiarLink: false,
      };
    case "safari_16":
      return {
        titulo: "Para receber notificações neste iPhone",
        texto: null,
        passos: ["Toque em Compartilhar (o quadrado com a seta, embaixo).", "Toque em Adicionar à Tela de Início.", PASSO_DO_ICONE],
        copiarLink: false,
      };
    case "chrome":
      return {
        titulo: "Para receber notificações neste iPhone",
        texto: null,
        passos: ["Toque em Compartilhar (no endereço, em cima, ou no menu ⋯).", "Toque em Adicionar à Tela de Início.", PASSO_DO_ICONE],
        copiarLink: false,
      };
    case "outro_navegador":
      return {
        titulo: "Para receber notificações neste iPhone",
        texto: null,
        passos: ["Toque em Compartilhar (no menu do navegador).", "Toque em Adicionar à Tela de Início.", PASSO_DO_ICONE],
        copiarLink: false,
      };
    case "ios_antigo":
      return {
        titulo: "Este iPhone não recebe notificações do painel",
        texto: "Precisa do iOS 16.4 ou mais novo. Dá para atualizar em Ajustes → Geral → Atualização de Software.",
        passos: [],
        copiarLink: false,
      };
  }
}

/** Como a Atendente fala do jeito de abrir. */
export const COMO_ABRE_O_PAINEL: Record<TipoDoPainelIphone, string> = {
  icone: "pelo ícone na tela de início",
  instagram: "dentro do Instagram",
  facebook: "dentro do Facebook",
  outro_app: "dentro de outro aplicativo",
  safari_26: "no Safari (iOS 26 ou mais novo)",
  safari_16: "no Safari (iOS 16.4 a 18)",
  chrome: "no Chrome",
  outro_navegador: "em outro navegador (Firefox, Edge…)",
  ios_antigo: "num iPhone com iOS anterior ao 16.4",
};
